import React from 'react';
import {t} from '@lingui/core/macro';
import {Trans} from '@lingui/react';

import MainModal from 'shared/ui/Modal/MainModal';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import {ExceededModal} from 'entities/user';
import LabelWithHint from 'shared/ui/FormGroup/LabelWithHint/LabelWithHint';
import LoaderCentered from 'shared/ui/LoaderCentered/LoaderCentered';
import {MonitorToggle} from 'entities/monitor';
import {PortMonitorModalMode} from 'features/port/lib/portMonitorHelper';
import {IPortMonitorListItem} from 'features/port/model/PortMonitor/IPortMonitor';
import usePortMonitorModal from 'features/port/model/usePortMonitorModal';

import PortMonitorForm from './PortMonitorForm/PortMonitorForm';
import PortMonitorBatchCreateForm from './PortMonitorForm/PortMonitorBatchCreateForm';

interface IPortMonitorModalProps {
    open: boolean,
    item?: IPortMonitorListItem,
    handleClose: () => void,
}

const getHeader = (mode: PortMonitorModalMode): string => {
    switch (mode) {
        case 'batch':
            return t`Создание мониторов портов`;
        case 'create':
            return t`Создание монитора порта`;
        default:
            return t`Редактирование монитора порта`;
    }
};

const PortMonitorModal: React.FC<IPortMonitorModalProps> = ({open, item, handleClose}) => {
    const {
        mode,
        isNew,
        isBatchMode,
        showToggle,
        canCreate,
        isFullItemLoading,
        fullItem,
        isExceededOpen,
        openExceeded,
        closeExceeded,
        toggleBatchMode,
        setShowResult,
        handleCloseAndReset,
    } = usePortMonitorModal({open, item, handleClose});

    const renderBody = () => {
        if (isBatchMode) {
            return canCreate
                ? (
                    <PortMonitorBatchCreateForm
                        handleClose={handleCloseAndReset}
                        onShowResultChange={setShowResult}
                    />
                )
                : (
                    <ExceededModal
                        isOpen={true}
                        handleClose={handleCloseAndReset}
                        type="port_monitors"
                    />
                );
        }

        if (!isNew && (isFullItemLoading || !fullItem)) {
            return (
                <ModalBodyWrapper>
                    <LoaderCentered active={true}/>
                </ModalBodyWrapper>
            );
        }

        return (
            <PortMonitorForm
                item={fullItem}
                onSuccess={handleCloseAndReset}
                onLimitExceeded={openExceeded}
                onClose={handleCloseAndReset}
            />
        );
    };

    return (
        <>
            <MainModal
                header={getHeader(mode)}
                open={open}
                handleClose={handleCloseAndReset}
                size="md"
            >
                {showToggle && (
                    <MonitorToggle
                        isBatchMode={isBatchMode}
                        handleModeToggle={toggleBatchMode}
                        label={
                            <LabelWithHint
                                label={<Trans id="add.portMonitor.modal.toggle.title" message="Адрес"/>}
                                hint={t`Формат ip:port, для IPv6 — [ipv6]:port.`}
                            />
                        }
                        hint={t`Каждый адрес с новой строки в формате ip:port, для IPv6 — [ipv6]:port.`}
                    />
                )}
                {renderBody()}
            </MainModal>
            <ExceededModal
                isOpen={isExceededOpen}
                handleClose={closeExceeded}
                type="port_monitors"
            />
        </>
    );
};

export default PortMonitorModal;
